/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import {
  modelLabAssetApi,
  modelLabDatasetApi,
  modelLabDeploymentApi,
  modelLabFeatureSetApi,
  modelLabTrainingJobApi,
} from '../../services/api';
import { ErrorAlert, LoadingSpinner, PageHeader } from '../common';
import {
  deploymentStatusColor,
  formatMetricSummary,
  trainingStatusColor,
} from './utils';

const ModelLabOverview: React.FC = () => {
  const datasetsQuery = useQuery({
    queryKey: ['model-lab-datasets-overview'],
    queryFn: () => modelLabDatasetApi.list({ page_size: 5 }),
  });
  const featureSetsQuery = useQuery({
    queryKey: ['model-lab-featuresets-overview'],
    queryFn: () => modelLabFeatureSetApi.list({ page_size: 5 }),
  });
  const assetsQuery = useQuery({
    queryKey: ['model-lab-assets-overview'],
    queryFn: () => modelLabAssetApi.list({ page_size: 5 }),
  });
  const jobsQuery = useQuery({
    queryKey: ['model-lab-jobs-overview'],
    queryFn: () => modelLabTrainingJobApi.list({ page_size: 5 }),
  });
  const deploymentsQuery = useQuery({
    queryKey: ['model-lab-deployments-overview'],
    queryFn: () => modelLabDeploymentApi.list({ page_size: 5 }),
  });

  const error =
    datasetsQuery.error ||
    featureSetsQuery.error ||
    assetsQuery.error ||
    jobsQuery.error ||
    deploymentsQuery.error;

  if (error) {
    return <ErrorAlert error={error as Error} />;
  }

  if (
    datasetsQuery.isLoading ||
    featureSetsQuery.isLoading ||
    assetsQuery.isLoading ||
    jobsQuery.isLoading ||
    deploymentsQuery.isLoading
  ) {
    return <LoadingSpinner message="Loading Model Lab workspace..." />;
  }

  const summaryCards = [
    {
      label: 'Datasets',
      value: datasetsQuery.data?.count || 0,
      helper: 'Versioned training data sources',
      to: '/ai/ml/datasets',
    },
    {
      label: 'Feature Sets',
      value: featureSetsQuery.data?.count || 0,
      helper: 'Curated feature engineering pipelines',
      to: '/ai/ml/features',
    },
    {
      label: 'Model Assets',
      value: assetsQuery.data?.count || 0,
      helper: 'Registries for ML, DL, and RL definitions',
      to: '/ai/ml/models',
    },
    {
      label: 'Training Jobs',
      value: jobsQuery.data?.count || 0,
      helper: 'Runtime executions and tracked metrics',
      to: '/ai/ml/training-jobs',
    },
    {
      label: 'Experiments',
      value: (jobsQuery.data?.results || []).filter((j) => j.status === 'COMPLETED').length,
      helper: 'Compare completed training runs',
      to: '/ai/ml/experiments',
    },
    {
      label: 'Deployments',
      value: deploymentsQuery.data?.count || 0,
      helper: 'Endpoint state and live inference',
      to: '/ai/ml/deployments',
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Model Lab"
        breadcrumbs={[
          { label: 'Automation', to: '/ai' },
          { label: 'Model Lab' },
        ]}
        actions={
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button component={RouterLink} to="/ai/ml/datasets" variant="outlined">
              Datasets
            </Button>
            <Button component={RouterLink} to="/ai/ml/features" variant="outlined">
              Features
            </Button>
            <Button component={RouterLink} to="/ai/ml/experiments" variant="outlined">
              Experiments
            </Button>
            <Button component={RouterLink} to="/ai/ml/training-jobs" variant="contained">
              New Training Flow
            </Button>
          </Stack>
        }
      />

      <Stack spacing={3}>
        <Alert severity="info">
          Model Lab provides end-to-end ML development: datasets, feature engineering,
          model registry, training jobs with rich metrics, experiment comparison,
          and deployment with live prediction testing.
        </Alert>

        <Grid container spacing={2}>
          {summaryCards.map((card) => (
            <Grid key={card.label} size={{ xs: 12, sm: 6, lg: 2 }}>
              <Card>
                <CardContent>
                  <Typography variant="overline" color="text.secondary">
                    {card.label}
                  </Typography>
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    {card.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {card.helper}
                  </Typography>
                  <Button component={RouterLink} to={card.to} size="small">
                    Open
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Runtime Support
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
                  <Chip label="ML runnable" color="success" />
                  <Chip label="DL scaffolded" color="warning" variant="outlined" />
                  <Chip label="RL scaffolded" color="warning" variant="outlined" />
                  <Chip label="No-code authoring" color="primary" variant="outlined" />
                  <Chip label="Restricted Python" color="primary" variant="outlined" />
                  <Chip label="Feature engineering" color="success" variant="outlined" />
                  <Chip label="Cross-validation" color="success" variant="outlined" />
                  <Chip label="Experiment comparison" color="success" variant="outlined" />
                  <Chip label="Live prediction" color="success" variant="outlined" />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Executable training currently targets scikit-learn ML jobs. DL and RL
                  registry objects can be created now so the surrounding workflow can be
                  designed before their execution runtimes land.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Recent Training Jobs
                </Typography>
                <List disablePadding>
                  {(jobsQuery.data?.results || []).map((job) => (
                    <ListItem key={job.id} divider disableGutters>
                      <ListItemText
                        primary={job.model_asset_name}
                        secondary={job.dataset_version_name}
                      />
                      <Stack direction="row" spacing={1} alignItems="center">
                        {formatMetricSummary(job.metrics).slice(0, 1).map((metric) => (
                          <Chip key={metric} label={metric} size="small" variant="outlined" />
                        ))}
                        <Chip
                          label={job.status}
                          size="small"
                          color={trainingStatusColor(job.status)}
                        />
                      </Stack>
                    </ListItem>
                  ))}
                  {(jobsQuery.data?.results || []).length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No training jobs yet. Use the training page to create and launch one.
                    </Typography>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Latest Assets
                </Typography>
                <List disablePadding>
                  {(assetsQuery.data?.results || []).map((asset) => (
                    <ListItem key={asset.id} divider disableGutters>
                      <ListItemText
                        primary={asset.name}
                        secondary={`${asset.family} • ${asset.framework} • ${asset.problem_type}`}
                      />
                      <Chip
                        size="small"
                        label={`v${asset.latest_version_number || 0}`}
                        variant="outlined"
                      />
                    </ListItem>
                  ))}
                  {(assetsQuery.data?.results || []).length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No model assets yet. Register one to define runtime specs and version history.
                    </Typography>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, lg: 6 }}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Deployment State
                </Typography>
                <List disablePadding>
                  {(deploymentsQuery.data?.results || []).map((deployment) => (
                    <ListItem key={deployment.id} divider disableGutters>
                      <ListItemText
                        primary={deployment.name}
                        secondary={deployment.endpoint_slug}
                      />
                      <Chip
                        label={deployment.status}
                        size="small"
                        color={deploymentStatusColor(deployment.status)}
                      />
                    </ListItem>
                  ))}
                  {(deploymentsQuery.data?.results || []).length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No deployments yet. Deployment state can be prepared here before it is wired into AI systems.
                    </Typography>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};

export default ModelLabOverview;